import { useTranslation } from 'react-i18next';

import { message } from 'antd';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { useMutation } from '@tanstack/react-query';
import { emailRegisterFinish, emailRegisterStart } from 'api';
import { AppRoutes } from 'config/routes';
import { EmailStartRegisterRequest } from 'models/api/auth/email-register-auth-request';
import { EmailStartAuthRequest } from 'models/api/auth/email-start-auth-request';
import { useState } from 'react';
import { handleAuth } from 'store/reducers/auth.reducer';
import { setStaffInviteId } from 'store/reducers/invite.conteragent';
import { uploadClient as appolloClient } from 'api/apollo-upload-client';
import { gql } from '@apollo/client';

interface InviteAuthProps {
  password: string;
}

const ACCEPT_INVITE = gql`
  mutation CoreEmployeeInvitations($invitationId: ID!) {
    coreEmployeeInvitations {
      acceptInvitation(input: { invitationId: $invitationId, isAccepted: true })
    }
  }
`;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function acceptStaffInvite(invitationId: string, accessToken: string) {
  let lastError: unknown;

  for (let attempt = 0; attempt < 4; attempt++) {
    if (attempt > 0) {
      await sleep(1000 * attempt);
    }

    try {
      await appolloClient.mutate({
        mutation: ACCEPT_INVITE,
        variables: { invitationId },
        context: {
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
        },
      });
      return;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError;
}

export default function useEmailStaffInvite() {
  const staffInviteToken = new URLSearchParams(window.location?.search || '').get('token');

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();

  const [isLoading, setLoading] = useState(false);

  const { mutateAsync: startRegister } = useMutation((data: EmailStartAuthRequest) =>
    emailRegisterStart(data),
  );

  const { mutateAsync: finishRegister } = useMutation((data: EmailStartRegisterRequest) =>
    emailRegisterFinish(data),
  );

  const setPassword = async ({ password }: InviteAuthProps) => {
    try {
      setLoading(true);

      const { token } = await startRegister({
        password,
        invite_id: staffInviteToken ?? undefined,
      });

      const result = await finishRegister({ password, data: {}, token });

      if (staffInviteToken && result?.access_token) {
        await acceptStaffInvite(staffInviteToken, result.access_token);
        dispatch(setStaffInviteId(''));
      }

      dispatch(handleAuth(result));
      navigate(state?.from ?? AppRoutes.Dashboard.Home + AppRoutes.Dashboard.Contracts.Home);
      return result.access_token;
    } catch (error: any) {
      message.error(error?.message || t('Something went wrong...'));
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { setPassword, isLoading };
}
